<script lang="ts">
	import type { ChoiceGroup, FoodMenuChoiceIssueReport, IDed, WithOptionsAndExtras } from "$lib/def/food-menu";
	import type { Addon, AddonGroup, Extra, ExtraGroup, Option, OptionGroup } from "proto/menu";
	import type { Subject, Subscription } from "rxjs";
	import type { ChangeEventHandler } from "svelte/elements";
	
	import { createEventDispatcher, onDestroy, onMount } from "svelte";
	import { formatCurrency } from "$lib/util/format";
	import { fly } from "svelte/transition";
	import { getItem } from "$lib/serv/core/local-store";

	import Checkboxes from "$lib/ui/form/Checkboxes.svelte";
	import Radio from "$lib/ui/form/Radio.svelte";
	import RestaurantMenuChoices from "./RestaurantMenuChoices.svelte";

	export let group: OptionGroup | ExtraGroup | AddonGroup;
	export let host: { id: string, name: string } | null = null;
	export let hostIndex: string = '';
	export let hostPosition: number = 0;
	export let preselected: (IDed & WithOptionsAndExtras)[] | undefined; // with extras and options if this choice is an addon
	export let reporter: Subject<FoodMenuChoiceIssueReport>;

	let selected: (IDed & WithOptionsAndExtras)[] = [];

	const dispatch = createEventDispatcher();

	$: allOptions = group.items.map(({ id }) => ({ id } as IDed));
	$: initialSelection(group, allOptions, preselected);

	let showError = '';
	let subReady: Subscription;
	let holdBackErrors = true;
	$: {
		subReady?.unsubscribe();
		subReady = reporter.subscribe(({report, highlight}) => {
			const er = getErrorMsg();
			let issues = er ? [er] : undefined;
			if (issues?.length) {
				if (highlight) {
					showError = issues[0]; // for now we only ever list one issue
					holdBackErrors = false;
				}
			} else {
				showError = '';
			}
			report(hostIndex, issues);
		});
	}

	function initialSelection(
		grp: OptionGroup | ExtraGroup | AddonGroup,
		allOptions: IDed[],
		preselected?: (IDed & WithOptionsAndExtras)[]
	) {
		const min = grp.minimum_select ?? 0;
		const max = grp.maximum_select ?? grp.items.length;
		if (preselected?.length) {
			const preIds = preselected.map(s => s.id);
			selected = allOptions.filter(item => preIds.includes(item.id));
			holdBackErrors = false;
		}
		if (!selected.length || selected.length < min) {
			// set defaults
			let updated = false;
			grp.items.forEach((item, index) => {
				const notYetSelected = selected.findIndex(s => s.id === item.id) === -1;
				const selectionSpotLeft = selected.length < max;
				if (item.default && notYetSelected && selectionSpotLeft) {
					selected.push(allOptions[index]);
					updated = true;
				}
			});
			if (updated) {
				setTimeout(() => dispatch('update', selected)); // without the setTimeout the host is not yet listening for events and will not receive the update
			}
		}
	}

	/**
	 * Dispaches an array of selected choices while following max and min selection rules.
	 * In case of an optional choice, if all are deselected and emtpy array is passed.
	 */
	const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const er = getErrorMsg();
		let issues = er ? [er] : undefined;
		if (!er) holdBackErrors = false;
		if (issues?.length && !holdBackErrors) {
			showError = issues[0];
		} else {
			showError = '';
		}
		if (!issues?.length) {
      dispatch('update', selected); // only dispatch if there are no issues (avoid useless quote)
    }
	}

	/**
	 * Finds the selected choice and updates the selected options or extras.
	 * This is used for addons that have options and extras.
	 * @param item The addon to update.
	 * @param prop The property to update, either 'options' or 'extras'.
	 * @param groups The array of selected options or extras.
	 */
	function updateSelect<T>(item: T & (Option | Extra | Addon), prop: 'options'|'extras', groups: ChoiceGroup[]) {
		const update = selected;
		const selectedItem = update.find(i => i.id === item.id) as IDed & WithOptionsAndExtras;
		if (selectedItem) {
			selectedItem[prop] = groups;
			dispatch('update', update);
		} else {
			dispatch('update', []);
		}
	}

	function getInnerGroups(item: any, prop: 'options'):OptionGroup[];
	function getInnerGroups(item: any, prop: 'extras'):ExtraGroup[];
	function getInnerGroups(item: any, prop: 'options' | 'addons' | 'extras') {
		if (prop in item) return item[prop] as (OptionGroup | ExtraGroup | AddonGroup)[];
	}

	function getErrorMsg() {
		if (typeof group.minimum_select === 'number' && selected.length < group.minimum_select) {
			return `Please select at least ${group.minimum_select}`;
		} else if (typeof group.maximum_select === 'number' && selected.length > group.maximum_select) {
			return `You may have ${group.maximum_select} at most, please remove ${selected.length - group.maximum_select}`;
		}
		return '';
	}

	onDestroy(() => {
		subReady?.unsubscribe();
		// dispatch('update', []); // from G, this is not good, but I not sure who needed it or why. Why would a component update its parent as it is being destroyed? This will also mean that the update event gets absolutely spammed when the menu gets unloaded.
	});
</script>

<div id={hostIndex} class="anchor"></div>

<div class="choice" class:error={showError} in:fly|global={{ x: -40, delay: hostPosition * 40 }}>

	{#if group.minimum_select >= 1 && group.maximum_select === 1}
		<div class="required label-xs bold m-0">REQUIRED</div>
	{:else if group.minimum_select === group.maximum_select}
		<div class="required label-xs bold m-0">REQUIRED (SELECT {group.maximum_select})</div>
	{:else if group.minimum_select >= 1}
		<div class="required label-xs bold m-0">REQUIRED (MAX {group.maximum_select})</div>
	{/if}

	<h6 class="body-l bold m-top-0">
		{group.label + (!!host ? ` for ${host.name}` : '')}
		{#if getItem('dev-mode')}
			- {hostIndex} min:{group.minimum_select??'-'} max:{group.maximum_select??'-'} defs:{group.items.filter(i => i.default).length}
		{/if}
	</h6>
	
	<div class="list">
		{#if group.minimum_select <= 1 && group.maximum_select === 1}
			{#each group.items as item, index}
				<Radio name={hostIndex} bind:group={selected[0]} value={allOptions[index]} {onChange} disabled={item.availability?.status === "sold_out"} >
					<div class="flex flex-nowrap">
						<div>{item.name}</div>
            {#if item.availability?.status === "sold_out"}
              <div class="bold">SOLD OUT</div>
            {:else}
						  <div class="bold">{formatCurrency(item.price)}</div>
            {/if}
					</div>
				</Radio>
			{/each}
		{:else}
			<Checkboxes name={hostIndex} {group} {allOptions} bind:selected={selected} {onChange} />
		{/if}
	</div>
</div>

<!-- ADDON options and extras-->
{#each group.items as item, index (item.id)}
	{#if selected.map(s => s.id).includes(item.id)}
		{@const options = getInnerGroups(item, 'options')}
		{#if options}
			{@const sel = preselected?.find(s => s.id === item.id)}
			<RestaurantMenuChoices groups={options} hostPosition={index + hostPosition} selectedGroups={sel?.options ?? []} host={{ id: item.id.toString(), name: item.name }} hostIndex={`${hostIndex}_s${item.id}_o`} on:update={e => updateSelect(item, 'options', e.detail)} {reporter}/>
		{/if}
		{@const extras = getInnerGroups(item, 'extras')}
		{#if extras}
			{@const sel = preselected?.find(s => s.id === item.id)}
			<RestaurantMenuChoices groups={extras} hostPosition={index + hostPosition + (options ? 1 : 0)} selectedGroups={sel?.extras ?? []} host={{ id: item.id.toString(), name: item.name }} hostIndex={`${hostIndex}_s${item.id}_e`} on:update={e => updateSelect(item, 'extras', e.detail)} {reporter}/>
		{/if}
	{/if}
{/each}

